import React, { useState } from 'react';
import { View, Text, Pressable, Picker, StyleSheet } from 'react-native';
import { Calendar, LocaleConfig } from 'react-native-calendars';
import { Ionicons } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

LocaleConfig.locales['pt-br'] = {
  monthNames: MONTHS,
  monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
  dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
  dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  today: 'Hoje',
};
LocaleConfig.defaultLocale = 'pt-br';

const formatBR = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// Amanhã em ISO no fuso LOCAL — toISOString() é UTC e, à noite no Brasil,
// pulava um dia (impedia selecionar amanhã como data mínima).
const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Hoje em ISO no fuso LOCAL (evita off-by-one perto da meia-noite).
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Calendar-based date picker for the admin panel. Emits an ISO 'YYYY-MM-DD' string.
// The header exposes month + year dropdowns so the user can jump directly.
const AdminDatePicker = ({ value, onChange, minDate, placeholder = 'Selecione uma data' }) => {
  const [open, setOpen] = useState(false);
  const min = minDate || tomorrowStr();
  // Sem valor selecionado, abre no mês de HOJE (não no minDate, que pode ser bem antigo).
  const initial = value || todayStr();
  const [visibleMonth, setVisibleMonth] = useState(`${initial.slice(0, 7)}-01`);

  const year = Number(visibleMonth.slice(0, 4));
  const month = Number(visibleMonth.slice(5, 7)); // 1-12
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 21 }, (_, i) => thisYear + i);

  const setYearMonth = (y, m) => setVisibleMonth(`${y}-${String(m).padStart(2, '0')}-01`);

  return (
    <View style={{ marginTop: 6 }}>
      <Pressable onPress={() => setOpen((o) => !o)} style={styles.trigger}>
        <Ionicons name="calendar-outline" size={18} color={ADMIN_COLORS.muted} />
        <Text style={[styles.triggerText, !value && { color: ADMIN_COLORS.placeholder }]}>
          {value ? formatBR(value) : placeholder}
        </Text>
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={ADMIN_COLORS.muted} />
      </Pressable>

      {open && (
        <View style={styles.calendarCard}>
          <View style={styles.headerRow}>
            <Picker
              selectedValue={month}
              onValueChange={(m) => setYearMonth(year, m)}
              style={[styles.headerPicker, { flex: 1.4 }]}
            >
              {MONTHS.map((name, i) => (
                <Picker.Item key={i} label={name} value={i + 1} />
              ))}
            </Picker>
            <Picker
              selectedValue={year}
              onValueChange={(y) => setYearMonth(y, month)}
              style={[styles.headerPicker, { flex: 1 }]}
            >
              {years.map((y) => (
                <Picker.Item key={y} label={String(y)} value={y} />
              ))}
            </Picker>
          </View>

          <Calendar
            key={visibleMonth}
            current={visibleMonth}
            minDate={min}
            onMonthChange={(mo) => setVisibleMonth(`${mo.year}-${String(mo.month).padStart(2, '0')}-01`)}
            onDayPress={(day) => {
              onChange(day.dateString);
              setOpen(false);
            }}
            markedDates={
              value ? { [value]: { selected: true, selectedColor: ADMIN_COLORS.accent } } : {}
            }
            renderHeader={() => null}
            theme={{
              backgroundColor: ADMIN_COLORS.surface,
              calendarBackground: ADMIN_COLORS.surface,
              todayTextColor: ADMIN_COLORS.accentDark,
              arrowColor: ADMIN_COLORS.accentDark,
              selectedDayBackgroundColor: ADMIN_COLORS.accent,
              selectedDayTextColor: '#ffffff',
              dayTextColor: ADMIN_COLORS.ink,
              textDisabledColor: '#C4C9D1',
              textSectionTitleColor: ADMIN_COLORS.muted,
              textDayFontFamily: ADMIN_FONT,
              textDayHeaderFontFamily: ADMIN_FONT,
            }}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    height: 44,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    paddingHorizontal: 14,
    backgroundColor: ADMIN_COLORS.surface,
    cursor: 'pointer',
  },
  triggerText: {
    flex: 1,
    color: ADMIN_COLORS.ink,
    fontFamily: ADMIN_FONT,
    fontSize: 15,
  },
  calendarCard: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 12,
    overflow: 'hidden',
    padding: 10,
    backgroundColor: ADMIN_COLORS.surface,
  },
  headerRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 6,
  },
  headerPicker: {
    height: 40,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 8,
    paddingHorizontal: 8,
    backgroundColor: ADMIN_COLORS.surface,
    color: ADMIN_COLORS.ink,
    fontFamily: ADMIN_FONT,
    fontSize: 14,
  },
});

export default AdminDatePicker;
